<script setup>
import Button from "primevue/button";
import { computed, onMounted, ref, watch } from "vue";
import { usePage } from "@inertiajs/vue3";
import axios from "axios";
import WizardButton from "../../../components/ui-components/WizardButton.vue";
import GoogleIcon from "../../../../login/components/GoogleIcon.vue";
import CalendarListView from "./CalendarListView.vue";

const props_page = usePage().props;
const model = defineModel();
const valid = defineModel('valid');

const calendars = ref([]);
const loadingCalendars = ref(false);
const error = ref(false);

const isConnected = computed(() => {
    if (!props_page.user || !model.value?.calendarProvider) return false;
    switch (model.value.calendarProvider) {
        case 'google': return !!props_page.user.google_id;
        case 'microsoft': return !!props_page.user.microsoft_id;
        default: return false;
    }
});

const connectAccounts = () => {
    document.location.href = '/connect/' + model.value.calendarProvider + '/calendar';
};

const fetchCalendars = (refresh = false) => {
    if (!model.value?.calendarProvider || !isConnected.value) return;

    loadingCalendars.value = true;
    error.value = false;

    const params = refresh ? '?refresh=true' : '';

    axios.get(`/app/forms/available_calendars/${model.value.calendarProvider}${params}`)
        .then(r => { calendars.value = r.data ?? []; })
        .catch(() => { error.value = true; })
        .finally(() => { loadingCalendars.value = false; });
};

watch(() => model.value?.calendarProvider, () => {
    calendars.value = [];
    fetchCalendars();
});

onMounted(() => fetchCalendars());
</script>

<template>
    <div class="flex flex-col gap-5 pb-6 pt-4">
        <div class="flex flex-col gap-4 items-start">
            <wizard-button @select="model.calendarProvider = 'google'" :active="model.calendarProvider==='google'" icon="pi pi-google" :title="$t('forms.calendar_google_title')">
                <template #icon>
                    <span class="w-[30px] h-[30px] p-2 bg-gray-200 dark:bg-gray-800 rounded">
                        <google-icon></google-icon>
                    </span>
                </template>
            </wizard-button>
            <wizard-button @select="model.calendarProvider = 'microsoft'" :active="model.calendarProvider==='microsoft'" icon="pi pi-microsoft" title="Microsoft 365"></wizard-button>
        </div>

        <template v-if="model.calendarProvider">
            <!-- Nepřipojený účet -->
            <div v-if="!isConnected">
                <p class="mb-4">{{ $t('forms.calendar_not_connected', { provider: model.calendarProvider[0].toUpperCase() + model.calendarProvider.slice(1) }) }} <span class="font-bold">{{ $t('forms.calendar_connect_warning') }}</span></p>
                <Button severity="secondary" :icon="'pi pi-'+model.calendarProvider" :label="$t('forms.calendar_connect_btn')" @click="connectAccounts"></Button>
            </div>

            <!-- Připojený účet -->
            <template v-else>
                <div class="border-b dark:border-gray-800"/>
                <div class="flex items-center justify-between">
                    <p>{{ $t('forms.calendar_select_hint') }}</p>
                    <Button
                        icon="pi pi-refresh"
                        size="small"
                        severity="secondary"
                        text
                        :loading="loadingCalendars"
                        @click="fetchCalendars(true)"
                        v-tooltip="$t('forms.calendar_refresh_tooltip')"
                    />
                </div>

                <CalendarListView
                    v-model="model.calendarId"
                    :calendars="calendars"
                    :loading="loadingCalendars"
                    :error="error"
                    :reconnect-providers="[model.calendarProvider]"
                    @refresh="fetchCalendars(true)"
                    @select="(cal) => { model.calendarTimezone = cal.timezone ?? null; valid = true }"
                />
            </template>
        </template>
    </div>
</template>
